import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import CartModel from '../../../../models/CartModel';
import ProductModelMC from '../../../../models/ProductModelMC';
import AsyncPaginatedSelectMC from '../../../../components/AsyncPaginatedSelectMC';
import CartAsyncPaginatedSelect from './CartAsyncPaginatedSelect';
import ButtonMC from '../../../../components/ButtonMC';
import NoActivityPlaceholder from './NoActivityPlaceholder';
import { formatCartPrice, cartActionErrorMessage } from './cartFormat';
import { showtoast } from '../../../../../util/toast';

const MAX_QUANTITY = 20;
const QUANTITY_OPTIONS = Array.from({ length: MAX_QUANTITY }, (_, index) => ({
  value: index + 1,
  label: String(index + 1)
}));

const parseSelectProducts = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.products)) return response.products;
  return [];
};

const optionLabel = (...parts) => parts.filter(Boolean).map(String).join(' · ');

const slimProduct = (product) => ({
  id: product.id,
  title: product.title || product.name,
  name: product.name,
  price: product.price
});

const slimCombination = (combination) => (
  combination
    ? { id: combination.id, name: combination.name, price: combination.price }
    : null
);

const productSelectOptions = (products) => (
  products.flatMap((product) => {
    const combinations = Array.isArray(product.product_variant_combinations)
      ? product.product_variant_combinations
      : [];
    if (!combinations.length) {
      return [{
        value: `p-${product.id}`,
        label: optionLabel(product.title || product.name),
        product: slimProduct(product),
        combination: null
      }];
    }

    return combinations.map((combination) => ({
      value: `p-${product.id}-v-${combination.id}`,
      label: optionLabel(product.title || product.name, combination.name),
      product: slimProduct(product),
      combination: slimCombination(combination)
    }));
  })
);

const selectedUnitPrice = (selection) => (
  Number(selection?.combination?.price ?? selection?.product?.price ?? 0)
);

const CartAdd = ({ customerId, onAdded }) => {
  const { t } = useTranslation('ChatbotIA');
  const { customer } = useSelector((reduxState) => reduxState.mainReducer);
  const { retailer_info } = useSelector((reduxState) => reduxState.retailerUsersReducer);
  const currency = retailer_info?.currency_code ? `${retailer_info.currency_code} ` : '';
  const customerIdToUse = customerId !== undefined ? customerId : customer?.id;
  const [selection, setSelection] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [resetKey, setResetKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const loadProductOptions = useCallback(async (inputValue) => {
    try {
      const response = await ProductModelMC.getSelectProducts({
        searchText: inputValue || '',
        productsQuery: ''
      });
      return {
        options: productSelectOptions(parseSelectProducts(response)).slice(0, 50),
        hasMore: false
      };
    } catch (_error) {
      return { options: [], hasMore: false };
    }
  }, []);

  const loadQuantityOptions = useCallback(async () => ({
    options: QUANTITY_OPTIONS,
    hasMore: false
  }), []);

  const handleAdd = () => {
    if (!customerIdToUse || !selection?.product) {
      showtoast(t('cart.playground.invalid_product'));
      return;
    }

    const parsedQuantity = parseInt(quantity, 10);
    const unitPrice = selectedUnitPrice(selection);
    if (!parsedQuantity) {
      showtoast(t('cart.playground.invalid_product'));
      return;
    }

    const item = {
      product_id: String(selection.product.id),
      name: selection.product.title || selection.product.name,
      quantity: parsedQuantity,
      unit_price: unitPrice,
      variant_id: selection.combination?.id ? String(selection.combination.id) : null,
      variant_name: selection.combination?.name || null
    };

    setSubmitting(true);
    CartModel.addItems(String(customerIdToUse), [item])
      .then(() => {
        showtoast(t('cart.playground.added'));
        setSelection(null);
        setQuantity(1);
        setResetKey((prev) => prev + 1);
        onAdded?.();
      })
      .catch((error) => {
        showtoast(cartActionErrorMessage(error, t, 'cart.playground.add_error'));
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  if (!customerIdToUse) {
    return <NoActivityPlaceholder />;
  }

  const unitPrice = selectedUnitPrice(selection);

  return (
    <div className="tw-wrapper" style={{ marginTop: 16, paddingBottom: 16 }}>
      <CartAsyncPaginatedSelect
        key={`product-${resetKey}`}
        label={t('cart.playground.fields.product')}
        placeholder={t('cart.playground.select_product')}
        loadOptions={loadProductOptions}
        onSelect={(_value, option) => {
          setSelection(option);
          setQuantity(1);
        }}
      />

      {selection?.product && (
        <>
          <div style={{ marginTop: 12 }}>
            <AsyncPaginatedSelectMC
              key={`quantity-${resetKey}-${selection.value}`}
              label={t('cart.playground.fields.quantity')}
              placeholder={t('cart.playground.select_quantity')}
              search={false}
              defaultValue={1}
              loadOptions={loadQuantityOptions}
              onSelect={(value) => setQuantity(Number(value))}
            />
          </div>

          <div
            className="d-flex align-items-center justify-content-between"
            style={{ marginTop: 8, padding: '0 4px', fontSize: 12, color: '#6b7280' }}
          >
            <span>{formatCartPrice(currency, unitPrice)}</span>
            <span className="font-600 text-gray-950">
              {formatCartPrice(currency, unitPrice * quantity)}
            </span>
          </div>
        </>
      )}

      <div style={{ marginTop: 12 }}>
        <ButtonMC
          text={t('cart.playground.add_to_cart')}
          type="button"
          size="medium"
          icon="shopping-cart-outline"
          iconPosition="left"
          disabled={submitting || !selection?.product}
          loading={submitting}
          onClick={handleAdd}
          className="tw-w-full tw-rounded-xl"
        />
      </div>
    </div>
  );
};

export default CartAdd;
